import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import productData from "../Datas/productData";
import siteConfig from "../config/siteConfig";
import { CartContext } from "./CartContext";
import "../css/productdetails.scss";

const formatPrice = (value) =>
  `${siteConfig.currency.symbol}${Number(value).toFixed(2)} ${siteConfig.currency.code}`;

const swatchPalette = [
  ["black", "#1d1d1b"],
  ["navy", "#263149"],
  ["indigo", "#344963"],
  ["denim", "#55738b"],
  ["sky", "#9fbfce"],
  ["blue", "#496f91"],
  ["white", "#f4f1e8"],
  ["natural", "#ddd0b5"],
  ["beige", "#c8ae83"],
  ["tan", "#a98459"],
  ["khaki", "#777052"],
  ["army", "#58603e"],
  ["sage", "#88937b"],
  ["green", "#4f6247"],
  ["orange", "#c96c36"],
  ["copper", "#9f5538"],
  ["brown", "#694735"],
  ["mocha", "#866759"],
  ["mustard", "#b1872e"],
  ["red", "#9b382f"],
  ["grey", "#a7a59f"],
  ["gray", "#a7a59f"],
];

const getSwatchTone = (name) =>
  swatchPalette.find(([label]) => name.toLowerCase().includes(label))?.[1] ||
  "#d4d1c7";

const getSwatchBackground = (name) => {
  const tones = name.split(/\s+(?:x|×)\s+/i).map(getSwatchTone);
  return tones.length > 1
    ? `linear-gradient(135deg, ${tones[0]} 0 50%, ${tones[1]} 50% 100%)`
    : tones[0];
};

const ProductDetail = () => {
  const { id } = useParams();
  const product = productData.find((item) => item.id === Number(id));
  const { addToCart, isWishlisted, toggleWishlist } = useContext(CartContext);
  const [selectedSize, setSelectedSize] = useState("");
  const [selectedColor, setSelectedColor] = useState("");
  const [selectedImage, setSelectedImage] = useState(0);
  const [quantity, setQuantity] = useState(1);
  const [activeTab, setActiveTab] = useState("description");
  const [imageLoaded, setImageLoaded] = useState(false);
  const [addedMessage, setAddedMessage] = useState("");
  const [showStickyAdd, setShowStickyAdd] = useState(false);
  const drawerCloseRef = useRef(null);

  useEffect(() => {
    if (!product) return;
    setSelectedSize(product.size[0] || "");
    setSelectedColor(product.colors[0] || "");
    setSelectedImage(0);
    setQuantity(1);
    setActiveTab("description");
    setImageLoaded(false);
    setAddedMessage("");
  }, [product]);

  useEffect(() => {
    const handleScroll = () => setShowStickyAdd(window.scrollY > 620);
    handleScroll();
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    if (!addedMessage) return undefined;
    const previousOverflow = document.body.style.overflow;
    const previousFocus = document.activeElement;
    const focusTimer = window.setTimeout(() => drawerCloseRef.current?.focus(), 0);
    const handleDialogKeys = (event) => {
      if (event.key === "Escape") {
        setAddedMessage("");
        return;
      }
      if (event.key !== "Tab") return;

      const focusable = document.querySelectorAll(
        ".product-mini-cart button, .product-mini-cart a"
      );
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last?.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first?.focus();
      }
    };

    document.body.style.overflow = "hidden";
    document.addEventListener("keydown", handleDialogKeys);
    return () => {
      window.clearTimeout(focusTimer);
      document.body.style.overflow = previousOverflow;
      document.removeEventListener("keydown", handleDialogKeys);
      previousFocus?.focus?.();
    };
  }, [addedMessage]);

  const recommendations = useMemo(() => {
    if (!product) return [];
    return productData
      .filter(
        (item) => item.id !== product.id && item.category === product.category
      )
      .slice(0, 4);
  }, [product]);

  const scrollToTop = () => window.scrollTo({ top: 0, behavior: "smooth" });

  if (!product) {
    return (
      <main className="product-missing">
        <p className="eyebrow">404 / Product</p>
        <h1>This item took a different trail.</h1>
        <p>The product may have sold out or moved to another collection.</p>
        <Link className="button button--primary" to="/shop">
          Back to shop
        </Link>
      </main>
    );
  }

  const handleAddToCart = () => {
    if (!product.inStock) return;
    addToCart(product.id, quantity, selectedSize, selectedColor);
    setAddedMessage(`${quantity} added to your cart.`);
  };

  const handleTabKeyDown = (event) => {
    if (!["ArrowLeft", "ArrowRight"].includes(event.key)) return;
    event.preventDefault();
    const nextTab = activeTab === "description" ? "sizing" : "description";
    setActiveTab(nextTab);
    document.getElementById(`${nextTab}-tab`)?.focus();
  };

  const handleColorKeyDown = (event, currentIndex) => {
    const supportedKeys = [
      "ArrowLeft",
      "ArrowRight",
      "ArrowUp",
      "ArrowDown",
      "Home",
      "End",
    ];
    if (!supportedKeys.includes(event.key)) return;
    event.preventDefault();

    let nextIndex = currentIndex;
    if (["ArrowLeft", "ArrowUp"].includes(event.key)) {
      nextIndex = (currentIndex - 1 + product.colors.length) % product.colors.length;
    } else if (["ArrowRight", "ArrowDown"].includes(event.key)) {
      nextIndex = (currentIndex + 1) % product.colors.length;
    } else if (event.key === "Home") {
      nextIndex = 0;
    } else if (event.key === "End") {
      nextIndex = product.colors.length - 1;
    }

    setSelectedColor(product.colors[nextIndex]);
    window.requestAnimationFrame(() => {
      document.querySelectorAll(".product-color-options button")[nextIndex]?.focus();
    });
  };

  return (
    <main className="product-page">
      <nav className="product-page__breadcrumb" aria-label="Breadcrumb">
        <Link to="/shop">Shop</Link>
        <span>/</span>
        <Link to={`/shop?category=${encodeURIComponent(product.category)}`}>
          {product.category}
        </Link>
        <span>/</span>
        <span aria-current="page">{product.name}</span>
      </nav>

      <div className="product-page__layout">
        <section className="product-gallery" aria-label="Product gallery">
          {product.images.length > 1 && (
            <div className="product-gallery__thumbs">
              {product.images.map((image, index) => (
                <button
                  key={image}
                  className={index === selectedImage ? "is-active" : ""}
                  type="button"
                  aria-label={`View ${product.name}, image ${index + 1}`}
                  aria-pressed={index === selectedImage}
                  onClick={() => {
                    setImageLoaded(false);
                    setSelectedImage(index);
                  }}
                >
                  <img src={image} alt="" />
                </button>
              ))}
            </div>
          )}
          <figure className={`product-gallery__main${imageLoaded ? " is-loaded" : ""}`}>
            <span className="product-gallery__loader">Loading image…</span>
            <img
              src={product.images[selectedImage]}
              alt={product.name}
              fetchpriority="high"
              onLoad={() => setImageLoaded(true)}
            />
          </figure>
        </section>

        <aside className="product-buybox">
          <div className="product-buybox__status">
            <span className={product.inStock ? "is-in-stock" : "is-sold-out"}>
              {product.inStock ? "In stock" : "Sold out"}
            </span>
            {product.newArrival && <span>New season</span>}
          </div>

          <h1>{product.name}</h1>
          <div className="product-buybox__price">
            {product.compareAtPrice && (
              <del>{formatPrice(product.compareAtPrice)}</del>
            )}
            <span className={product.compareAtPrice ? "is-sale" : ""}>
              {formatPrice(product.price)}
            </span>
          </div>

          <div className="product-buybox__option">
            <div className="product-buybox__option-label">
              Color <strong>{selectedColor}</strong>
            </div>
            <div
              className="product-color-options"
              role="radiogroup"
              aria-label="Choose a color"
            >
              {product.colors.map((color, index) => (
                <button
                  key={color}
                  className={selectedColor === color ? "is-selected" : ""}
                  type="button"
                  role="radio"
                  aria-checked={selectedColor === color}
                  tabIndex={selectedColor === color ? 0 : -1}
                  aria-label={color}
                  title={color}
                  onClick={() => setSelectedColor(color)}
                  onKeyDown={(event) => handleColorKeyDown(event, index)}
                >
                  <span style={{ background: getSwatchBackground(color) }} />
                </button>
              ))}
            </div>
          </div>

          <div className="product-buybox__option">
            <div className="product-buybox__option-heading">
              <label htmlFor="product-size">Size</label>
              <button type="button" onClick={() => setActiveTab("sizing")}>
                Size guide
              </button>
            </div>
            <select
              id="product-size"
              value={selectedSize}
              onChange={(event) => setSelectedSize(event.target.value)}
            >
              {product.size.map((size) => (
                <option key={size} value={size}>
                  {size}
                </option>
              ))}
            </select>
          </div>

          <div className="product-buybox__actions">
            <div className="product-quantity">
              <button
                type="button"
                aria-label="Decrease quantity"
                onClick={() => setQuantity((current) => Math.max(1, current - 1))}
              >
                −
              </button>
              <span aria-live="polite">{quantity}</span>
              <button
                type="button"
                aria-label="Increase quantity"
                onClick={() => setQuantity((current) => Math.min(10, current + 1))}
              >
                +
              </button>
            </div>
            <button
              className="product-add"
              type="button"
              disabled={!product.inStock}
              onClick={handleAddToCart}
            >
              {product.inStock ? "Add to cart" : "Sold out"}
            </button>
            <button
              className={`product-wish${isWishlisted(product.id) ? " is-active" : ""}`}
              type="button"
              aria-label={
                isWishlisted(product.id)
                  ? "Remove from wishlist"
                  : "Add to wishlist"
              }
              aria-pressed={isWishlisted(product.id)}
              onClick={() => toggleWishlist(product.id)}
            >
              <span aria-hidden="true">♡</span>
            </button>
          </div>

          <p className="product-buybox__shipping">
            Free domestic shipping on orders over {siteConfig.currency.symbol}
            {siteConfig.shippingThreshold}.
            Easy returns within {siteConfig.returnWindowDays} days.
          </p>

          <div className="product-tabs">
            <div className="product-tabs__list" role="tablist">
              <button
                id="description-tab"
                type="button"
                role="tab"
                aria-controls="product-info-panel"
                aria-selected={activeTab === "description"}
                tabIndex={activeTab === "description" ? 0 : -1}
                onClick={() => setActiveTab("description")}
                onKeyDown={handleTabKeyDown}
              >
                Description
              </button>
              <button
                id="sizing-tab"
                type="button"
                role="tab"
                aria-controls="product-info-panel"
                aria-selected={activeTab === "sizing"}
                tabIndex={activeTab === "sizing" ? 0 : -1}
                onClick={() => setActiveTab("sizing")}
                onKeyDown={handleTabKeyDown}
              >
                Sizing
              </button>
            </div>
            <div
              id="product-info-panel"
              className="product-tabs__panel"
              role="tabpanel"
              aria-labelledby={`${activeTab}-tab`}
            >
              {activeTab === "description" ? (
                <>
                  {product.description.map((paragraph) => (
                    <p key={paragraph}>{paragraph}</p>
                  ))}
                  <ul>
                    {product.Features.map((feature) => (
                      <li key={feature}>{feature}</li>
                    ))}
                  </ul>
                  <dl>
                    {Object.entries(product.attributes).map(([label, value]) => (
                      <div key={label}>
                        <dt>{label}</dt>
                        <dd>{value}</dd>
                      </div>
                    ))}
                  </dl>
                </>
              ) : (
                <>
                  <p>
                    Designed for an easy, movement-friendly fit. Choose your
                    usual size, or size down for a closer silhouette.
                  </p>
                  <div className="product-tabs__sizes">
                    {product.size.map((size) => (
                      <span key={size}>{size}</span>
                    ))}
                  </div>
                  <p>
                    Need a hand? Email {siteConfig.contact.email} with your
                    height, usual size, and preferred fit.
                  </p>
                </>
              )}
            </div>
          </div>
        </aside>
      </div>

      {recommendations.length > 0 && (
        <section className="product-recommendations" aria-labelledby="recommendations-title">
          <h2 id="recommendations-title">You may also like</h2>
          <div>
            {recommendations.map((item) => (
              <article key={item.id}>
                <Link to={`/shopDetails/${item.id}`} onClick={scrollToTop}>
                  <img src={item.image} alt={item.name} loading="lazy" />
                  <h3>{item.name}</h3>
                  <p>{formatPrice(item.price)}</p>
                </Link>
              </article>
            ))}
          </div>
        </section>
      )}

      {addedMessage && (
        <>
          <button
            className="product-mini-cart__scrim"
            type="button"
            aria-label="Close cart preview"
            onClick={() => setAddedMessage("")}
          />
          <aside
            className="product-mini-cart"
            role="dialog"
            aria-modal="true"
            aria-labelledby="product-mini-cart-title"
          >
            <div className="product-mini-cart__heading">
              <p id="product-mini-cart-title">Added to your cart</p>
              <button
                ref={drawerCloseRef}
                type="button"
                aria-label="Close cart preview"
                onClick={() => setAddedMessage("")}
              >
                ×
              </button>
            </div>
            <div className="product-mini-cart__line">
              <img src={product.image} alt="" />
              <div>
                <strong>{product.name}</strong>
                <span>{selectedColor} / {selectedSize}</span>
                <span>{addedMessage}</span>
                <b>{formatPrice(product.price * quantity)}</b>
              </div>
            </div>
            <Link className="product-mini-cart__checkout" to="/checkout">
              Check out
            </Link>
            <Link className="product-mini-cart__view" to="/cartpage">
              View cart
            </Link>
          </aside>
        </>
      )}

      <div className={`product-sticky-add${showStickyAdd ? " is-visible" : ""}`}>
        <div>
          <strong>{product.name}</strong>
          <span>{formatPrice(product.price)}</span>
        </div>
        <button type="button" disabled={!product.inStock} onClick={handleAddToCart}>
          {product.inStock ? "Add to cart" : "Sold out"}
        </button>
      </div>
    </main>
  );
};

export default ProductDetail;
